import { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { supabase } from '../../lib/supabaseClient';
import { mxWeekBounds, mxTodayStr, mondayOfStr } from '../../lib/mxWeek';
import { parseHoursCapError } from '../../lib/hoursCapError';

const PAGE_SIZE = 15;

/* ── Constants ─────────────────────────────────────── */
const WORK_START_MIN = 9 * 60;   // 09:00 in minutes
const WORK_END_MIN = 18 * 60;    // 18:00 in minutes

/* ── Helpers ──────────────────────────────────────── */
function todayStr() {
  // MX-anchored: toISOString() is UTC and drifts a day ahead after 18:00
  // Mexico City, which skewed the week summary and date-filter defaults.
  return mxTodayStr();
}

function formatDate(dateStr) {
  if (!dateStr) return '';
  const [y, m, d] = dateStr.split('-');
  return `${d}/${m}/${y}`;
}

function formatTime(timeStr) {
  if (!timeStr) return '';
  return timeStr.slice(0, 5);
}

/** Returns the first day of the current MX month as YYYY-MM-DD */
function firstOfMonth() {
  return `${mxTodayStr().slice(0, 7)}-01`;
}

function toMin(timeStr) {
  const [h, m] = timeStr.split(':').map(Number);
  return h * 60 + m;
}

/**
 * Splits a time range into normal hours (09:00–18:00, Mon–Fri) and extended
 * hours. Weekends (Sat/Sun) count entirely as extended hours, regardless of
 * time of day. Returns { normal, extended, total } all in decimal hours.
 */
function calcHoursBreakdown(logDate, startStr, endStr) {
  if (!startStr || !endStr) return { normal: 0, extended: 0, total: 0 };
  const startMin = toMin(startStr);
  const endMin = toMin(endStr);
  if (endMin <= startMin) return { normal: 0, extended: 0, total: 0 };
  const totalMins = endMin - startMin;
  const day = new Date(logDate + 'T00:00:00').getDay();
  const isWeekend = day === 0 || day === 6;
  const normalStart = Math.max(startMin, WORK_START_MIN);
  const normalEnd = Math.min(endMin, WORK_END_MIN);
  const normalMins = isWeekend ? 0 : Math.max(0, normalEnd - normalStart);
  const extMins = totalMins - normalMins;
  const r = (n) => Math.round((n / 60) * 100) / 100;
  return { normal: r(normalMins), extended: r(extMins), total: r(totalMins) };
}

/* ── Filter Picker Modal ─────────────────────────── */
function FilterPickerModal({ open, title, options, value, onSelect, onClose, labelKey }) {
  const [search, setSearch] = useState('');
  const inputRef = useRef(null);
  const backdropRef = useRef(null);

  useEffect(() => {
    if (open) {
      setSearch('');
      setTimeout(() => inputRef.current?.focus(), 50);
    }
  }, [open]);

  useEffect(() => {
    if (!open) return;
    function handleKey(e) { if (e.key === 'Escape') onClose(); }
    document.addEventListener('keydown', handleKey);
    return () => document.removeEventListener('keydown', handleKey);
  }, [open, onClose]);

  if (!open) return null;

  const getLabel = (o) => typeof labelKey === 'function' ? labelKey(o) : o[labelKey || 'name'];

  const filtered = search.trim()
    ? options.filter((o) => getLabel(o).toLowerCase().includes(search.toLowerCase()))
    : options;

  return (
    <div className="act-fpm-backdrop" ref={backdropRef} onClick={(e) => { if (e.target === backdropRef.current) onClose(); }}>
      <div className="act-fpm-modal">
        <div className="act-fpm-header">
          <h3 className="act-fpm-title">{title}</h3>
          <button className="act-fpm-close" onClick={onClose} aria-label="Cerrar">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" /></svg>
          </button>
        </div>
        <div className="act-fpm-search-wrap">
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="11" cy="11" r="8" /><line x1="21" y1="21" x2="16.65" y2="16.65" /></svg>
          <input
            ref={inputRef}
            type="text"
            className="act-fpm-search"
            placeholder="Buscar…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>
        <ul className="act-fpm-list">
          <li>
            <button
              className={`act-fpm-item${!value ? ' act-fpm-item--active' : ''}`}
              onClick={() => { onSelect(''); onClose(); }}
            >
              <span className="act-fpm-item-label">Todos</span>
            </button>
          </li>
          {filtered.map((o) => {
            const label = getLabel(o);
            const isActive = String(o.id) === String(value);
            return (
              <li key={o.id}>
                <button
                  className={`act-fpm-item${isActive ? ' act-fpm-item--active' : ''}`}
                  onClick={() => { onSelect(o.id); onClose(); }}
                >
                  <span className="act-fpm-item-label">{label}</span>
                  {isActive && (
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><polyline points="20 6 9 17 4 12" /></svg>
                  )}
                </button>
              </li>
            );
          })}
          {filtered.length === 0 && (
            <li className="act-fpm-empty">Sin resultados</li>
          )}
        </ul>
      </div>
    </div>
  );
}

/* ── Component ────────────────────────────────────── */
function TimeLogReport({ userId, role, onBack }) {
  const isManagerOrLeader = role === 'Gerente' || role === 'Líder';

  // Filters
  const [dateFrom, setDateFrom] = useState(firstOfMonth());
  const [dateTo, setDateTo] = useState(todayStr());
  const [filterReqId, setFilterReqId] = useState('');
  const [filterActivityId, setFilterActivityId] = useState('');
  const [filterUserId, setFilterUserId] = useState('');
  const [filterClientId, setFilterClientId] = useState('');

  // Data
  const [logs, setLogs] = useState([]);
  const [activities, setActivities] = useState([]);
  const [allUsers, setAllUsers] = useState([]);
  const [clients, setClients] = useState([]);
  const [requirements, setRequirements] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(0);

  // Picker modals
  const [userPickerOpen, setUserPickerOpen] = useState(false);
  const [reqPickerOpen, setReqPickerOpen] = useState(false);
  const [clientPickerOpen, setClientPickerOpen] = useState(false);

  // Edit modal
  const [editingLog, setEditingLog] = useState(null);
  const [editForm, setEditForm] = useState({});
  const [editSaving, setEditSaving] = useState(false);
  const [editError, setEditError] = useState('');

  // Delete confirm
  const [deletingLog, setDeletingLog] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState('');

  // Summary
  const [summary, setSummary] = useState({ week: 0, month: 0 });

  // Past-day lock (Líder & Desarrollador): MX-anchored current-week bounds plus
  // the set of active per-day grants that un-lock a past day. Keyed by
  // `${owner_user_id}|${log_date}` so the multi-user report respects owner-keying.
  const [grantKeys, setGrantKeys] = useState(() => new Set());
  const [minuteTick, setMinuteTick] = useState(0);

  // Recompute "today"/week every minute so the midnight rollover and grant
  // expiry are reflected without a manual reload.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const weekBounds = useMemo(() => mxWeekBounds(), [minuteTick]);

  useEffect(() => {
    const id = setInterval(() => setMinuteTick((t) => t + 1), 60000);
    return () => clearInterval(id);
  }, []);

  const fetchGrants = useCallback(async () => {
    if (role === 'Gerente') { setGrantKeys(new Set()); return; }
    // RLS scopes this: Líder sees all grants, Desarrollador only their own.
    const { data } = await supabase
      .from('time_log_permissions')
      .select('user_id, log_date')
      .is('revoked_at', null)
      .gt('expires_at', new Date().toISOString())
      .gte('log_date', weekBounds.weekStartStr)
      .lte('log_date', weekBounds.weekEndStr);
    setGrantKeys(new Set((data || []).map((r) => `${r.user_id}|${r.log_date}`)));
  }, [role, weekBounds.weekStartStr, weekBounds.weekEndStr]);

  useEffect(() => { fetchGrants(); }, [fetchGrants, minuteTick]);

  /* ── Load catalogs ── */
  useEffect(() => {
    async function loadCatalogs() {
      const [actRes, usersRes, clientsRes] = await Promise.all([
        supabase.from('activities').select('id, name, hour_type_id, hour_types(id, name)').eq('active', true).order('name'),
        isManagerOrLeader
          ? supabase.from('users').select('id, full_name').eq('active', true).order('full_name')
          : Promise.resolve({ data: [] }),
        supabase.from('clients').select('id, name').eq('active', true).order('name'),
      ]);
      setActivities(actRes.data || []);
      setAllUsers(usersRes.data || []);
      setClients(clientsRes.data || []);
    }
    loadCatalogs();
  }, [isManagerOrLeader]);

  /* ── Load requirements for filter dropdown (includes allow_extended_hours for edit validation) ── */
  useEffect(() => {
    async function loadReqs() {
      if (isManagerOrLeader && !filterUserId) {
        // No user filter — load ALL requirements so Gerente/Líder can filter by any requirement
        const { data } = await supabase
          .from('requirements')
          .select('id, reference_id, allow_extended_hours')
          .order('reference_id');
        setRequirements(data || []);
        return;
      }
      const targetUser = isManagerOrLeader ? filterUserId : userId;
      if (!targetUser) { setRequirements([]); return; }
      // A user's requirements = those assigned as dev (requirement_devs) OR as
      // estimator (estimator_user_id). Both feed the filter dropdown and the
      // edit-modal extended-hours lookup (see saveEdit), so an estimator can
      // filter/edit their estimator-requirement logs correctly.
      const [devRes, estRes] = await Promise.all([
        supabase
          .from('requirement_devs')
          .select('requirement_id, requirement:requirements!requirement_id(id, reference_id, allow_extended_hours)')
          .eq('user_id', targetUser),
        supabase
          .from('requirements')
          .select('id, reference_id, allow_extended_hours')
          .eq('estimator_user_id', targetUser),
      ]);
      const reqs = [
        ...(devRes.data || []).filter(r => r.requirement).map(r => r.requirement),
        ...(estRes.data || []),
      ];
      const seen = new Set();
      const unique = reqs.filter(r => { if (seen.has(r.id)) return false; seen.add(r.id); return true; });
      unique.sort((a, b) => a.reference_id.localeCompare(b.reference_id));
      setRequirements(unique);
    }
    loadReqs();
  }, [userId, filterUserId, isManagerOrLeader]);

  /* ── Fetch logs ── */
  const fetchLogs = useCallback(async () => {
    if (!userId && !isManagerOrLeader) return;
    setLoading(true);

    let query = supabase
      .from('time_logs')
      .select(`
        id, log_date, start_time, end_time, total_hours,
        is_extended_hours, extended_hours, description,
        activity:activities!activity_id(id, name, hour_types(name)),
        requirement:requirements!requirement_id(id, reference_id, description, allow_extended_hours, project:projects!project_id(id, name, client:clients!client_id(id, name))),
        user:users!user_id(id, full_name)
      `)
      .gte('log_date', dateFrom)
      .lte('log_date', dateTo)
      .order('log_date', { ascending: false })
      .order('start_time', { ascending: false });

    if (!isManagerOrLeader) {
      query = query.eq('user_id', userId);
    } else if (filterUserId) {
      query = query.eq('user_id', filterUserId);
    }

    if (filterReqId) query = query.eq('requirement_id', filterReqId);
    if (filterActivityId) query = query.eq('activity_id', Number(filterActivityId));

    if (filterClientId) {
      // time_logs has no client column — resolve it via requirement → project →
      // client, then narrow the logs query to those requirement ids.
      const { data: reqRows } = await supabase
        .from('requirements')
        .select('id, project:projects!project_id!inner(client_id)')
        .eq('project.client_id', filterClientId);
      const ids = (reqRows || []).map(r => r.id);
      if (ids.length === 0) {
        setLogs([]);
        setLoading(false);
        setPage(0);
        setSummary({ week: 0, month: 0 });
        return;
      }
      query = query.in('requirement_id', ids);
    }

    const { data } = await query;
    setLogs(data || []);
    setLoading(false);
    setPage(0);

    // Summary
    const currentMonday = mondayOfStr(todayStr());
    const monthStart = firstOfMonth();

    let weekTotal = 0, monthTotal = 0;
    (data || []).forEach((l) => {
      const h = (l.total_hours || 0) + (l.extended_hours || 0);
      if (l.log_date >= monthStart) monthTotal += h;
      if (l.log_date >= currentMonday) weekTotal += h;
    });
    setSummary({ week: Math.round(weekTotal * 100) / 100, month: Math.round(monthTotal * 100) / 100 });
  }, [userId, role, dateFrom, dateTo, filterReqId, filterActivityId, filterUserId, filterClientId, isManagerOrLeader]);

  useEffect(() => { fetchLogs(); }, [fetchLogs]);

  /* ── Pagination ── */
  const totalPages = Math.ceil(logs.length / PAGE_SIZE);
  const pagedLogs = logs.slice(page * PAGE_SIZE, (page + 1) * PAGE_SIZE);

  /* ── Edit / Delete permissions ──
   * Gerente: never locked. Líder & Desarrollador: may only modify today →
   * end of the current MX week, unless a Gerente has granted a per-day
   * permission for that owner+date. Desarrollador is further limited to its
   * own logs. Mirrors the time_logs RLS so the buttons match what will save. */
  function isDayUnlocked(log) {
    if (log.log_date >= weekBounds.todayStr && log.log_date <= weekBounds.weekEndStr) return true;
    return grantKeys.has(`${log.user?.id}|${log.log_date}`);
  }

  function canEditLog(log) {
    if (role === 'Gerente') return true;
    if (role === 'Desarrollador' && log.user?.id !== userId) return false;
    if (role !== 'Líder' && role !== 'Desarrollador') return false;
    return isDayUnlocked(log);
  }

  /* ── Edit handlers ── */
  function openEdit(log) {
    setEditingLog(log);
    setEditForm({
      startTime: formatTime(log.start_time),
      endTime: formatTime(log.end_time),
      activityId: String(log.activity?.id || ''),
      requirementId: log.requirement?.id || '',
      description: log.description || '',
    });
    setEditError('');
  }

  async function saveEdit() {
    setEditSaving(true);
    setEditError('');

    if (!editForm.activityId) {
      setEditError('Selecciona un tipo de actividad');
      setEditSaving(false);
      return;
    }

    const selectedAct = activities.find(a => a.id === Number(editForm.activityId));
    const isNA = selectedAct?.hour_types?.name === 'N/A';

    // Activity-type ↔ requirement pairing: Facturables / No Facturables must
    // be logged under a requirement (early feedback; the DB trigger
    // fn_enforce_activity_requirement_type also enforces this on UPDATE).
    if (!isNA && !editForm.requirementId) {
      setEditError('Las actividades Facturables o No Facturables deben registrarse bajo un requerimiento.');
      setEditSaving(false);
      return;
    }

    // Times must land on half-hour boundaries (:00 or :30) — activities are
    // tracked in 30-minute blocks, so 10:30–11:35 is not valid.
    if (toMin(editForm.startTime) % 30 !== 0 || toMin(editForm.endTime) % 30 !== 0) {
      setEditError('Las horas deben registrarse en intervalos de 30 minutos (en punto o y media).');
      setEditSaving(false);
      return;
    }

    const breakdown = calcHoursBreakdown(editingLog.log_date, editForm.startTime, editForm.endTime);
    if (breakdown.total <= 0) {
      setEditError('La hora fin debe ser posterior a la hora inicio');
      setEditSaving(false);
      return;
    }

    if (!editForm.description.trim()) {
      setEditError('Descripción requerida');
      setEditSaving(false);
      return;
    }

    // Past-day lock re-check for Líder & Desarrollador (RLS is the final gate,
    // but give early feedback). Gerente is exempt.
    if (role !== 'Gerente' && !isDayUnlocked(editingLog)) {
      setEditError('No puedes modificar este día. Es un día pasado y requiere un permiso temporal otorgado por un Gerente.');
      setEditSaving(false);
      return;
    }

    // Extended hours permission check (early feedback; the DB trigger also
    // enforces this on UPDATE). N/A activities save with requirement_id =
    // null, so check the effective requirement, not the form selection.
    if (breakdown.extended > 0) {
      const effectiveReqId = isNA ? null : editForm.requirementId;
      const selectedReq = effectiveReqId ? requirements.find(r => r.id === effectiveReqId) : null;
      if (!selectedReq?.allow_extended_hours) {
        setEditError(
          effectiveReqId
            ? 'Este requerimiento no tiene autorización para registrar horas fuera del horario laboral (antes de las 9:00, después de las 18:00 o en fin de semana).'
            : 'Las actividades sin requerimiento no pueden registrarse fuera del horario laboral (antes de las 9:00, después de las 18:00 o en fin de semana).'
        );
        setEditSaving(false);
        return;
      }
    }

    // Fetch other logs on same day for overlap and 24h cap checks
    const { data: existing, error: fetchErr } = await supabase
      .from('time_logs')
      .select('id, total_hours, extended_hours, start_time, end_time')
      .eq('user_id', editingLog.user?.id)
      .eq('log_date', editingLog.log_date);

    if (fetchErr) {
      setEditError(fetchErr.message || 'Error al validar');
      setEditSaving(false);
      return;
    }

    const otherLogs = (existing || []).filter(r => r.id !== editingLog.id);

    // Overlap check: new time range must not intersect any other log on the same day
    const newStart = toMin(editForm.startTime);
    const newEnd = toMin(editForm.endTime);
    const overlapping = otherLogs.find(r => {
      const oStart = toMin(r.start_time);
      const oEnd = toMin(r.end_time);
      return newStart < oEnd && newEnd > oStart;
    });
    if (overlapping) {
      setEditError(`El horario se superpone con otro registro (${overlapping.start_time?.slice(0, 5)}–${overlapping.end_time?.slice(0, 5)})`);
      setEditSaving(false);
      return;
    }

    // 24h/day cap
    const dayOther = otherLogs.reduce((sum, r) => sum + (r.total_hours || 0) + (r.extended_hours || 0), 0);
    if (dayOther + breakdown.total > 24) {
      setEditError(`Excede el límite de 24h para este día (${dayOther.toFixed(1)}h en otros registros)`);
      setEditSaving(false);
      return;
    }

    const isExtended = breakdown.extended > 0;

    const { error, count } = await supabase
      .from('time_logs')
      .update({
        start_time: editForm.startTime,
        end_time: editForm.endTime,
        total_hours: breakdown.normal,        // normal hours only (not full duration)
        activity_id: Number(editForm.activityId),
        requirement_id: isNA ? null : (editForm.requirementId || null),
        is_extended_hours: isExtended,
        extended_hours: isExtended ? breakdown.extended : null,
        description: editForm.description.trim(),
      }, { count: 'exact' })
      .eq('id', editingLog.id);

    setEditSaving(false);
    if (error) {
      // The requirement hours-cap trigger fires on UPDATE too — map its
      // HORAS_CAP_* codes to a readable message instead of the raw error.
      const capErr = parseHoursCapError(error.message);
      if (capErr) {
        setEditError(
          capErr.type === 'null_cap'
            ? `El requerimiento ${capErr.refId} no tiene horas de desarrollo estimadas. Contacta al líder del requerimiento para que las asigne.`
            : `Excede el límite de horas Facturables del requerimiento ${capErr.refId} (${capErr.cap}h). Disponible: ${capErr.remaining.toFixed(1)}h, intentas usar: ${capErr.incoming.toFixed(1)}h.`
        );
      } else {
        setEditError(error.message);
      }
      return;
    }
    if (count === 0) {
      // RLS rejected silently — log is outside current week or user lacks permission
      setEditError('No se pudo guardar el cambio. El registro puede estar fuera de la semana actual o no tienes permiso para modificarlo.');
      return;
    }
    setEditingLog(null);
    fetchLogs();
  }

  /* ── Delete handlers ── */
  async function confirmDelete() {
    setDeleting(true);
    setDeleteError('');

    // Past-day lock re-check for Líder & Desarrollador (Gerente is exempt)
    if (role !== 'Gerente' && !isDayUnlocked(deletingLog)) {
      setDeleteError('No puedes eliminar este día. Es un día pasado y requiere un permiso temporal otorgado por un Gerente.');
      setDeleting(false);
      return;
    }

    const { error, count } = await supabase
      .from('time_logs')
      .delete({ count: 'exact' })
      .eq('id', deletingLog.id);

    setDeleting(false);
    if (error) {
      setDeleteError(error.message || 'Error al eliminar');
      return;
    }
    if (count === 0) {
      setDeleteError('No se pudo eliminar el registro. Puede que ya no tengas permiso para modificar registros de semanas anteriores.');
      return;
    }
    setDeletingLog(null);
    setDeleteError('');
    fetchLogs();
  }

  // N/A activities never carry a requirement — gray the select so the user
  // sees the pairing instead of having their selection silently nulled on save
  const editActIsNA = activities.find(a => a.id === Number(editForm.activityId))?.hour_types?.name === 'N/A';

  // Group activities for edit form
  const groupedActivities = activities.reduce((acc, act) => {
    const group = act.hour_types?.name || 'Otros';
    if (!acc[group]) acc[group] = [];
    acc[group].push(act);
    return acc;
  }, {});

  /* ── Render ─────────────────────────────────────── */
  return (
    <>
    <div className="act-report-view">
      {/* Header */}
      <div className="cat-crud-header">
        <button type="button" className="cat-back-btn" onClick={onBack}>
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="15 18 9 12 15 6" /></svg>
          Volver
        </button>
        <h1 className="cat-crud-title">Reporte Personal</h1>
      </div>

      {/* Summary cards */}
      <div className="act-summary-row">
        <div className="act-summary-card">
          <span className="act-summary-label">Horas esta semana</span>
          <span className="act-summary-value">{summary.week}h</span>
        </div>
        <div className="act-summary-card">
          <span className="act-summary-label">Horas este mes</span>
          <span className="act-summary-value">{summary.month}h</span>
        </div>
        <div className="act-summary-card">
          <span className="act-summary-label">Registros en período</span>
          <span className="act-summary-value">{logs.length}</span>
        </div>
      </div>

      {/* Filters */}
      <div className="act-filters">
        <div className="act-filter-group">
          <label className="act-filter-label">Desde</label>
          <input type="date" className="cat-input" value={dateFrom}
            onChange={(e) => setDateFrom(e.target.value)} max={todayStr()} />
        </div>
        <div className="act-filter-group">
          <label className="act-filter-label">Hasta</label>
          <input type="date" className="cat-input" value={dateTo}
            onChange={(e) => setDateTo(e.target.value)} max={todayStr()} />
        </div>
        {isManagerOrLeader && (
          <div className="act-filter-group">
            <label className="act-filter-label">Recurso</label>
            <button
              type="button"
              className={`act-fpm-trigger${filterUserId ? ' act-fpm-trigger--active' : ''}`}
              onClick={() => setUserPickerOpen(true)}
            >
              <span className="act-fpm-trigger-label">
                {filterUserId ? (allUsers.find(u => u.id === filterUserId)?.full_name || 'Todos') : 'Todos'}
              </span>
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><polyline points="6 9 12 15 18 9" /></svg>
            </button>
          </div>
        )}
        <div className="act-filter-group">
          <label className="act-filter-label">Cliente</label>
          <button
            type="button"
            className={`act-fpm-trigger${filterClientId ? ' act-fpm-trigger--active' : ''}`}
            onClick={() => setClientPickerOpen(true)}
          >
            <span className="act-fpm-trigger-label">
              {filterClientId ? (clients.find(c => String(c.id) === String(filterClientId))?.name || 'Todos') : 'Todos'}
            </span>
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><polyline points="6 9 12 15 18 9" /></svg>
          </button>
        </div>
        <div className="act-filter-group">
          <label className="act-filter-label">Requerimiento</label>
          <button
            type="button"
            className={`act-fpm-trigger${filterReqId ? ' act-fpm-trigger--active' : ''}`}
            onClick={() => setReqPickerOpen(true)}
          >
            <span className="act-fpm-trigger-label">
              {filterReqId ? (requirements.find(r => r.id === filterReqId)?.reference_id || 'Todos') : 'Todos'}
            </span>
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><polyline points="6 9 12 15 18 9" /></svg>
          </button>
        </div>
        <div className="act-filter-group">
          <label className="act-filter-label">Actividad</label>
          <select className="cat-select" value={filterActivityId}
            onChange={(e) => setFilterActivityId(e.target.value)}>
            <option value="">Todas</option>
            {activities.map(a => (
              <option key={a.id} value={a.id}>{a.name}</option>
            ))}
          </select>
        </div>
      </div>

      {/* Table */}
      {loading ? (
        <div className="cat-loading">Cargando registros...</div>
      ) : logs.length === 0 ? (
        <div className="cat-empty">No se encontraron registros para los filtros seleccionados.</div>
      ) : (
        <>
          <div className="cat-table-wrap">
            <table className="cat-table">
              <thead>
                <tr>
                  <th>Fecha</th>
                  {isManagerOrLeader && <th>Recurso</th>}
                  <th>Cliente</th>
                  <th>Folio</th>
                  <th>Actividad</th>
                  <th>Tipo</th>
                  <th>Inicio</th>
                  <th>Fin</th>
                  <th>Horas</th>
                  <th>Ext.</th>
                  <th>Descripción</th>
                  <th>Acciones</th>
                </tr>
              </thead>
              <tbody>
                {pagedLogs.map((log) => (
                  <tr key={log.id}>
                    <td>{formatDate(log.log_date)}</td>
                    {isManagerOrLeader && <td>{log.user?.full_name || '—'}</td>}
                    <td>{log.requirement?.project?.client?.name || '—'}</td>
                    <td>{log.requirement?.reference_id || '—'}</td>
                    <td>{log.activity?.name || '—'}</td>
                    <td>
                      <span className={`act-type-badge act-type-badge--${(log.activity?.hour_types?.name || '').replace(/[^a-zA-Z]/g, '').toLowerCase()}`}>
                        {log.activity?.hour_types?.name || '—'}
                      </span>
                    </td>
                    <td>{formatTime(log.start_time)}</td>
                    <td>{formatTime(log.end_time)}</td>
                    <td>{log.total_hours}h</td>
                    <td>{log.extended_hours ? `${log.extended_hours}h` : '—'}</td>
                    <td className="act-desc-cell" title={log.description || ''}>{log.description || '—'}</td>
                    <td>
                      {canEditLog(log) ? (
                        <div className="cat-actions">
                          <button
                            type="button"
                            className="cat-action-btn cat-action-edit"
                            title="Editar"
                            onClick={() => openEdit(log)}
                          >
                            <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" /><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" /></svg>
                          </button>
                          <button
                            type="button"
                            className="cat-action-btn cat-action-delete"
                            title="Eliminar"
                            onClick={() => { setDeletingLog(log); setDeleteError(''); }}
                          >
                            <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="3 6 5 6 21 6" /><path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" /></svg>
                          </button>
                        </div>
                      ) : (
                        <span className="act-readonly-tag">Solo lectura</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="cat-pagination-wrap">
              <span className="cat-page-info">
                {page * PAGE_SIZE + 1}–{Math.min((page + 1) * PAGE_SIZE, logs.length)} de {logs.length}
              </span>
              <div className="cat-pagination">
                <button className="cat-page-nav" disabled={page === 0}
                  onClick={() => setPage(p => p - 1)}>
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="15 18 9 12 15 6" /></svg>
                </button>
                {Array.from({ length: totalPages }, (_, i) => (
                  <button
                    key={i}
                    className={`cat-page-btn ${i === page ? 'cat-page-btn--active' : ''}`}
                    onClick={() => setPage(i)}
                  >{i + 1}</button>
                ))}
                <button className="cat-page-nav" disabled={page >= totalPages - 1}
                  onClick={() => setPage(p => p + 1)}>
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="9 6 15 12 9 18" /></svg>
                </button>
              </div>
            </div>
          )}
        </>
      )}
    </div>

    {/* ── Modals (rendered outside .act-report-view so their fixed
         positioning resolves against the viewport, not the animated
         wrapper's transform containing block) ── */}

    {/* ── Edit Modal ── */}
    {editingLog && (
        <>
          <div className="cat-backdrop" onClick={() => !editSaving && setEditingLog(null)} />
          <div className="cat-modal">
            <h2 className="cat-modal-title">Editar Registro</h2>
            <div className="cat-modal-form">
              <div className="act-edit-info">
                <span>Fecha: <strong>{formatDate(editingLog.log_date)}</strong></span>
                <span>Recurso: <strong>{editingLog.user?.full_name}</strong></span>
              </div>

              <div className="cat-form-group">
                <label className="cat-form-label">Tipo de actividad</label>
                <select className="cat-select" value={editForm.activityId}
                  onChange={(e) => setEditForm({ ...editForm, activityId: e.target.value })}>
                  <option value="">Selecciona</option>
                  {Object.entries(groupedActivities).map(([group, acts]) => (
                    <optgroup key={group} label={group}>
                      {acts.map(a => <option key={a.id} value={a.id}>{a.name}</option>)}
                    </optgroup>
                  ))}
                </select>
              </div>

              <div className="cat-form-group">
                <label className="cat-form-label">Requerimiento</label>
                <select className="cat-select" value={editActIsNA ? '' : editForm.requirementId}
                  disabled={editActIsNA}
                  onChange={(e) => setEditForm({ ...editForm, requirementId: e.target.value })}>
                  <option value="">Sin requerimiento</option>
                  {requirements.map(r => (
                    <option key={r.id} value={r.id}>{r.reference_id}</option>
                  ))}
                </select>
              </div>

              <div className="act-time-row">
                <div className="cat-form-group">
                  <label className="cat-form-label">Hora inicio</label>
                  <input type="time" className="cat-input" step="1800"
                    value={editForm.startTime}
                    onChange={(e) => setEditForm({ ...editForm, startTime: e.target.value })} />
                </div>
                <div className="cat-form-group">
                  <label className="cat-form-label">Hora fin</label>
                  <input type="time" className="cat-input" step="1800"
                    value={editForm.endTime}
                    onChange={(e) => setEditForm({ ...editForm, endTime: e.target.value })} />
                </div>
                <div className="cat-form-group">
                  <label className="cat-form-label">Total</label>
                  <input
                    type="text"
                    className="cat-input act-input-readonly"
                    readOnly
                    tabIndex={-1}
                    value={(() => {
                      const b = calcHoursBreakdown(editingLog.log_date, editForm.startTime, editForm.endTime);
                      if (b.extended > 0) return `${b.total}h (${b.extended}h ext.)`;
                      return `${b.total}h`;
                    })()}
                  />
                </div>
              </div>

              <div className="cat-form-group">
                <label className="cat-form-label">Descripción</label>
                <textarea className="cat-input act-textarea"
                  value={editForm.description} rows={3}
                  onChange={(e) => setEditForm({ ...editForm, description: e.target.value })} />
              </div>

              {editError && <div className="cat-error-banner">{editError}</div>}

              <div className="cat-modal-actions">
                <button type="button" className="cat-btn-cancel" onClick={() => setEditingLog(null)} disabled={editSaving}>
                  Cancelar
                </button>
                <button type="button" className="cat-btn-primary" disabled={editSaving} onClick={saveEdit}>
                  {editSaving && <span className="cat-spinner" />}
                  {editSaving ? 'Guardando...' : 'Guardar Cambios'}
                </button>
              </div>
            </div>
          </div>
        </>
      )}

      {/* ── Recurso Picker ── */}
      {isManagerOrLeader && (
        <FilterPickerModal
          open={userPickerOpen}
          title="Filtrar por Recurso"
          options={allUsers}
          value={filterUserId}
          onSelect={(id) => { setFilterUserId(id); setFilterReqId(''); }}
          onClose={() => setUserPickerOpen(false)}
          labelKey="full_name"
        />
      )}

      {/* ── Cliente Picker ── */}
      <FilterPickerModal
        open={clientPickerOpen}
        title="Seleccionar Cliente"
        options={clients}
        value={filterClientId}
        onSelect={(id) => { setFilterClientId(id); setFilterReqId(''); }}
        onClose={() => setClientPickerOpen(false)}
        labelKey="name"
      />

      {/* ── Requerimiento Picker ── */}
      <FilterPickerModal
        open={reqPickerOpen}
        title="Filtrar por Requerimiento"
        options={requirements}
        value={filterReqId}
        onSelect={(id) => setFilterReqId(id)}
        onClose={() => setReqPickerOpen(false)}
        labelKey="reference_id"
      />

      {/* ── Delete Confirm ── */}
      {deletingLog && (
        <>
          <div className="cat-backdrop" onClick={() => !deleting && (setDeletingLog(null), setDeleteError(''))} />
          <div className="cat-confirm">
            <h3 className="cat-confirm-title">Eliminar Registro</h3>
            <p className="cat-confirm-msg">
              Se eliminará el registro del {formatDate(deletingLog.log_date)} — {deletingLog.activity?.name || ''}.
              Esta acción no se puede deshacer.
            </p>
            {deleteError && <div className="cat-error-banner" style={{ marginTop: '12px' }}>{deleteError}</div>}
            <div className="cat-confirm-actions">
              <button
                type="button"
                className="cat-btn-cancel"
                onClick={() => { setDeletingLog(null); setDeleteError(''); }}
                disabled={deleting}
              >
                Cancelar
              </button>
              <button type="button" className="cat-btn-danger" disabled={deleting} onClick={confirmDelete}>
                {deleting && <span className="cat-spinner" />}
                {deleting ? 'Eliminando...' : 'Eliminar'}
              </button>
            </div>
          </div>
        </>
      )}
    </>
  );
}

export default TimeLogReport;
